import { useEffect, useState } from 'react';
import { Loader2, FileCheck2 } from 'lucide-react';
import { API_URL } from '@/config/api';

// Super admin: who has accepted the Terms/DPA, who still hasn't, and a printable certificate per acceptance.
export default function LegalAcceptances() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [wording, setWording] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/legal/acceptances`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(setData)
      .catch(() => setError('Could not load acceptances'));
    fetch(`${API_URL}/api/legal/terms`, { credentials: 'include' }).then(r => (r.ok ? r.json() : null)).then(setWording).catch(() => {});
  }, []);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return <Loader2 className="w-6 h-6 text-pink-400 animate-spin" />;

  const fmt = (iso) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-6">
      {!data.gate_enabled && (
        <p className="text-sm rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2">
          The acceptance gate is <strong>off</strong>: centre users are not being asked to accept yet. Turn it on with LEGAL_GATE_ENABLED=1 on the server once the wording is signed off.
        </p>
      )}
      <p className="text-sm text-slate-500">
        Current version <strong>{data.version}</strong>. Centre users are asked to accept on their next visit. Account owners (admins) accept the Terms and the Data Processing Agreement for their business; staff accept the Terms.
      </p>

      {wording && (
        <details className="border border-slate-200 rounded-xl px-3 py-2">
          <summary className="cursor-pointer text-sm font-medium text-slate-800">Read the current wording (Terms + Data Processing Agreement)</summary>
          <div className="mt-3 space-y-2 max-h-[60vh] overflow-y-auto">
            {[...wording.terms, { heading: 'DATA PROCESSING AGREEMENT', body: '' }, ...wording.dpa].map(s => (
              <div key={s.heading}>
                <p className="text-sm font-semibold text-slate-800">{s.heading}</p>
                {s.body && <p className="text-sm text-slate-600">{s.body}</p>}
              </div>
            ))}
          </div>
        </details>
      )}

      <div>
        <h3 className="font-semibold text-slate-800 mb-2">Not accepted yet ({data.pending.length})</h3>
        {data.pending.length === 0 ? <p className="text-sm text-slate-400">Everyone is up to date.</p> : (
          <div className="border border-slate-200 rounded-xl divide-y divide-slate-100">
            {data.pending.map(u => (
              <div key={u.user_id} className="px-3 py-2 text-sm flex flex-wrap justify-between gap-2">
                <span className="text-slate-800">{u.name} <span className="text-slate-400">· {u.email}</span></span>
                <span className="text-slate-500">{u.business_name || u.business_id} · {u.role}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-semibold text-slate-800 mb-2">Acceptances ({data.acceptances.length})</h3>
        {data.acceptances.length === 0 ? <p className="text-sm text-slate-400">None yet.</p> : (
          <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-x-auto">
            {data.acceptances.map(a => (
              <div key={a.acceptance_id} className="px-3 py-2 text-sm flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-slate-800">{a.user_name} <span className="text-slate-400">· {a.user_email}</span></p>
                  <p className="text-xs text-slate-500">{a.business_name} · {a.scope.join(' + ').toUpperCase()} · v{a.version} · {fmt(a.accepted_at)} · IP {a.ip_address}</p>
                </div>
                <a
                  href={`${API_URL}/api/legal/acceptances/${a.acceptance_id}/certificate`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-pink-600 hover:text-pink-700 text-xs font-medium flex items-center gap-1"
                >
                  <FileCheck2 className="w-3.5 h-3.5" /> Certificate
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
